import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

import { parsePayload } from 'pages/Vendors/VendorManage/Tabs/CategoryTab/lib';
import ControlComponent from 'components/ControlComponent';
import { removeControlFile, uploadControlFiles } from 'api/assessment';
import { stripHtmlFromText } from 'utils/html';
import { useVendorManageContext } from 'hooks/useVendorManageContext';
import { API_STATUS } from 'constants/api';
import { ControlType } from 'constants/controlType';
import { useFileUploadProcess } from 'components/FileUploadIndicator/useFileUploadProcess';

import './categoryForm.scss';

const CategoryForm = ({ category, assessmentId }) => {
  const { onChangeAssessment } = useVendorManageContext();
  const assessmentData = useSelector((state) => state.assessment);
  const { isFileUploading } = assessmentData;
  const { vendorId } = useParams();
  const dispatch = useDispatch();
  const { abortController, startUploading, finishUploding, renderConfirmationModal } =
    useFileUploadProcess();

  const getNewControls = (value, controlId) => {
    const newControls = category.controls?.map((control) =>
      control.id === controlId ? { ...control, answer: value } : control,
    );
    const newControl = newControls.find((control) => control.id === controlId);

    return [newControl, newControls];
  };

  const handleUpload = async (files, controlId) => {
    const { id } = category;
    if (vendorId) {
      startUploading();
      const { payload } = await dispatch(
        uploadControlFiles({
          files,
          controlId,
          categoryId: id,
          assessmentId,
          abortController,
        }),
      );
      finishUploding();

      if (payload.status === API_STATUS.SUCCESS) {
        const newFiles = category.controls.map((control) => {
          if (control.id === controlId) {
            return {
              ...control,
              answer: [...control.answer, ...files],
            };
          }
          return control;
        });
        const [newControl] = getNewControls(files, controlId);

        onChangeAssessment(newFiles, assessmentId, newControl.id);
      }
    } else {
      const newFiles = category.controls.map((control) => {
        if (control.id === controlId) {
          return {
            ...control,
            answer: [...control.answer, ...files],
          };
        }
        return control;
      });
      const [newControl] = getNewControls(files, controlId);

      onChangeAssessment(newFiles, assessmentId, newControl.id);
    }
  };

  const onChange = (value, controlId) => {
    const [newControl] = getNewControls(value, controlId);
    onChangeAssessment([newControl], assessmentId, newControl.id);
  };

  const handleRemove = async (fileId, controlId) => {
    if (vendorId) {
      await dispatch(removeControlFile({ fileId, controlId, assessmentId })).unwrap();
    }

    const controlAnswers = category.controls.find((control) => control.id === controlId).answer;
    const newFiles = category.controls.map((control) => {
      if (control.id === controlId) {
        return {
          ...control,
          answer: controlAnswers.filter((answer) => answer.id !== fileId),
        };
      }
      return control;
    });

    onChangeAssessment(newFiles, assessmentId, controlId);
  };

  const renderControl = (control) => {
    const { answerData, title: label, description, id } = control;
    const payload = parsePayload(
      control,
      control.answer,
      onChange,
      handleUpload,
      handleRemove,
      isFileUploading,
    );

    if (category.app) {
      payload.disabled = true;
    }

    if (control['control_answer_type'] === ControlType.FileUpload) {
      payload.browse = true;
    }

    const rawLabel = stripHtmlFromText(label).toLowerCase();
    return (
      <>
        <ControlComponent
          key={id}
          type={answerData?.type}
          isSimple
          data={{
            label,
            description,
            assessmentId,
            payload: {
              ...payload,
              'data-test': `vendor-manage-${kebabCase(rawLabel)}`,
            },
          }}
        />
        {renderConfirmationModal()}
      </>
    );
  };

  const { title } = category;
  return (
    <div className="category-form">
      <div className="category-form__header">{title}</div>
      <div className="category-form__controls">
        {category.controls?.filter((control) => control.enabled)?.map(renderControl)}
      </div>
    </div>
  );
};

CategoryForm.propTypes = {
  category: PropTypes.any,
  assessmentId: PropTypes.string,
};

export default CategoryForm;
